import { format } from 'date-fns';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TargetCostData } from '@/types/target-cost';
import { formatCurrency, formatPercentage } from '@/utils/format';
import { calculateTargetCostMetrics } from '@/utils/target-cost-calculations';
import { PDF_COLORS, PDF_STYLES } from '@/utils/pdf-styles';
import { logError } from '@/utils/logger';

/**
 * Exports Target Cost data to PDF file.
 * Uses calculateTargetCostMetrics for all derived values.
 */
export async function exportTargetCostToPDF(data: TargetCostData): Promise<void> {
  try {
    const projectName = data.details.projectName || 'Untitled Project';
    const doc = new jsPDF();
    const metrics = calculateTargetCostMetrics(data);

    // Add Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...PDF_COLORS.primary);
    doc.text(`${projectName} - Target Cost Report`, 14, 20);

    // Add Timestamp
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...PDF_COLORS.muted);
    doc.text(`Generated on ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 30);

    // Add branding
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...PDF_COLORS.muted);
    doc.text('Generated by QS Tools', doc.internal.pageSize.width - 14, 20, { align: 'right' });

    // Project Details Section
    autoTable(doc, {
      startY: 40,
      head: [['Project Details', '']],
      body: [
        ['Project Name', projectName],
        ['Client Name', data.details.clientName || 'Not set'],
        [
          'Start Date',
          data.details.startDate
            ? format(new Date(data.details.startDate), 'dd/MM/yyyy')
            : 'Not set',
        ],
        [
          'End Date',
          data.details.endDate
            ? format(new Date(data.details.endDate), 'dd/MM/yyyy')
            : 'Not set',
        ],
        [
          'Reporting Period',
          data.details.reportingPeriodStart && data.details.reportingPeriodEnd
            ? `${format(new Date(data.details.reportingPeriodStart), 'dd/MM/yyyy')} to ${format(
                new Date(data.details.reportingPeriodEnd),
                'dd/MM/yyyy'
              )}`
            : 'Not set',
        ],
        ['Contract Form', data.details.contractForm || 'Not set'],
      ],
      ...PDF_STYLES.table,
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto' }
      },
      didParseCell: function(cellData) {
        if (cellData.section === 'body' && cellData.row.index % 2 === 1) {
          cellData.cell.styles.fillColor = [248, 248, 248];
        }
      }
    });

    // Costs Section
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Target and Actual Costs', '']],
      body: [
        ['Total of the Prices', formatCurrency(data.costs.totalPrices ?? 0)],
        ['Final PWDD', formatCurrency(data.costs.finalPWDD ?? 0)],
      ],
      ...PDF_STYLES.table,
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto', halign: 'right' }
      },
      didParseCell: function(cellData) {
        if (cellData.section === 'body' && cellData.row.index % 2 === 1) {
          cellData.cell.styles.fillColor = [248, 248, 248];
        }
      }
    });

    // Metrics Section
    const variance = (data.costs.totalPrices ?? 0) - (data.costs.finalPWDD ?? 0);
    const isOverrun = variance < 0;

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Key Metrics', '']],
      body: [
        ['Pain/Gain Amount', formatCurrency(metrics.painGainAmount)],
        ['Total Contractor Share', formatCurrency(metrics.totalContractorShare)],
        ['Contractor Margin', formatPercentage(metrics.contractorMargin)],
        ['Cost Variance', formatPercentage(metrics.costVariance)],
        [isOverrun ? 'Cost Overrun' : 'Savings', formatCurrency(Math.abs(variance))],
      ],
      ...PDF_STYLES.table,
      theme: 'grid',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { cellWidth: 'auto', halign: 'right' }
      },
      didParseCell: function(cellData) {
        if (cellData.section === 'body' && cellData.row.index % 2 === 1) {
          cellData.cell.styles.fillColor = [248, 248, 248];
        }
        // Highlight savings/overrun row
        if (cellData.section === 'body' && cellData.row.index === 4) {
          if (isOverrun) {
            cellData.cell.styles.textColor = [220, 38, 38]; // red
          } else if (variance > 0) {
            cellData.cell.styles.textColor = [22, 163, 74]; // green
          }
        }
      }
    });

    // Pain/Gain Share Ranges Section
    const rangeRows = metrics.rangeCalculations.map((range) => {
      const rangeLabel = range.type === 'less-than'
        ? 'Less Than'
        : range.type === 'greater-than'
          ? 'Greater Than'
          : `${range.startPercentage}% - ${range.endPercentage}%`;

      const fromValue = range.type === 'less-than' ? '-' : `${range.startPercentage}%`;
      const toValue = range.type === 'greater-than' ? '-' : `${range.endPercentage}%`;

      return [
        rangeLabel,
        fromValue,
        toValue,
        `${range.sharePercentage}%`,
        formatCurrency(range.amountInRange),
        formatCurrency(range.contractorShare),
      ];
    });

    // Add totals row
    const totalAmountInRange = metrics.rangeCalculations.reduce(
      (sum, range) => sum + range.amountInRange,
      0
    );
    rangeRows.push([
      'TOTAL',
      '',
      '',
      '',
      formatCurrency(totalAmountInRange),
      formatCurrency(metrics.totalContractorShare),
    ]);

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Range', 'From', 'To', 'Share', 'Amount in Range', "Contractor's Share"]],
      body: rangeRows,
      ...PDF_STYLES.table,
      theme: 'grid',
      margin: { left: 14, right: 14 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 35 },
        1: { cellWidth: 25, halign: 'right' },
        2: { cellWidth: 25, halign: 'right' },
        3: { cellWidth: 25, halign: 'right' },
        4: { cellWidth: 40, halign: 'right' },
        5: { cellWidth: 40, halign: 'right' }
      },
      didParseCell: function(cellData) {
        const isLastRow = cellData.row.index === rangeRows.length - 1;
        if (isLastRow) {
          cellData.cell.styles.fillColor = PDF_COLORS.primary;
          cellData.cell.styles.textColor = PDF_COLORS.white;
          cellData.cell.styles.fontStyle = 'bold';
        } else if (cellData.section === 'body' && cellData.row.index % 2 === 1) {
          cellData.cell.styles.fillColor = [248, 248, 248];
        }
      }
    });

    // Footer Section
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...PDF_COLORS.muted);
      doc.text(
        `Generated on ${format(new Date(), 'dd/MM/yyyy HH:mm')}`,
        14,
        doc.internal.pageSize.height - 20
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 20,
        doc.internal.pageSize.height - 20,
        { align: 'right' }
      );
    }

    // Save the PDF
    const filename = `${projectName.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_target_cost_${format(
      new Date(),
      'yyyy-MM-dd'
    )}.pdf`;
    doc.save(filename);
  } catch (error) {
    logError('Target Cost PDF Export failed:', error);
    throw error instanceof Error
      ? error
      : new Error('Failed to export PDF report');
  }
}
